import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// Estado del cajón monedero automático. Mantiene:
//   - `config`: la config actual (paths, workerId, timeout…) + schema
//     para pintar dinámicamente el wizard.
//   - `pendingTx`: mapa transactionId → snapshot para las transacciones
//     abiertas — se llena por el POST /charge y por el hub SignalR.
//     El PaymentDialog lee esto para decidir cuándo cerrar el modal
//     de espera y cómo continuar (aplicar pago, mostrar error, etc).
//   - `history`: últimas transacciones cerradas — panel Caja/Arqueo.
//
// Extensible a multi-provider: el `providerCode` viaja en cada snapshot,
// así el UI puede pintar el nombre del cajón (Sanjosat, Glory, IP…)
// sin acoplarse a un provider concreto.

const initialState = {
  loading: false,
  error: null,
  config: null,           // { providerCode, configJson, status, alternativePaymentId, schema }
  configLoadedFor: null,  // providerCode del último fetch OK — evita refetch en cada mount
  testResult: null,       // { success, message }
  pendingTx: {},          // { [transactionId]: snapshotDto }
  history: [],
  // Último status de conectividad del hardware reportado por el Worker
  // (via cashdrawer-device-status). Snapshot: { supported, reachable,
  // method, latencyMs?, port?, message, at? }.
  deviceStatus: null,
};

const slice = createSlice({
  name: "cashdrawer",
  initialState,
  reducers: {
    startLoading(state) { state.loading = true; state.error = null; },
    hasError(state, action) { state.loading = false; state.error = action.payload; },
    setConfig(state, action) {
      state.config = action.payload;
      state.configLoadedFor = action.payload?.providerCode || null;
      state.loading = false;
    },
    invalidateConfig(state) {
      state.configLoadedFor = null;
    },
    setTestResult(state, action) { state.testResult = action.payload; },
    // Upsert de la transacción (llega del hub SignalR o del POST /charge).
    upsertTx(state, action) {
      const tx = action.payload;
      if (!tx?.id) return;
      state.pendingTx[tx.id] = tx;
      // Si terminal, sacarla del "pending" tras un frame — la dejamos
      // brevemente para que los subscriptores puedan reaccionar.
    },
    dropTx(state, action) {
      const id = action.payload;
      delete state.pendingTx[id];
    },
    setHistory(state, action) { state.history = action.payload || []; },
    setDeviceStatus(state, action) {
      state.deviceStatus = action.payload
        ? { ...action.payload, at: Date.now() }
        : null;
    },
  },
});

export default slice.reducer;
export const {
  upsertTx,
  dropTx,
  startLoading,
  hasError,
  setConfig,
  setTestResult,
  setHistory,
  setDeviceStatus,
  invalidateConfig,
} = slice.actions;

// ─── Thunks ───────────────────────────────────────────────────────────

/**
 * 2026-08-24 · Cachea por providerCode. Pasa `{force:true}` para
 * refetch tras save/desconectar. Sin cache: cada mount de PaymentDialog
 * (una vez por mesa) disparaba un GET /cashdrawer/config/sanjosat
 * incluso si la integración no está contratada. Además, si
 * `activeCodesLoaded` está cargado y no contiene `sanjosat`, evitamos
 * la request completamente y dejamos config = { status: "inactive" }
 * — no hay integración, no tiene sentido preguntar.
 */
export const fetchCashDrawerConfig = (providerCode = "sanjosat", { force = false } = {}) =>
  async (dispatch, getState) => {
    if (!force) {
      const st = getState().cashdrawer;
      if (st?.configLoadedFor === providerCode) return;
      // Gate por activeCodes: si el store ya sabe que la integración
      // no está activa, ni siquiera llamamos al backend.
      const integrations = getState().integrations;
      if (
        integrations?.activeCodesLoaded
        && !(integrations.activeCodes || []).includes(providerCode)
      ) {
        dispatch(setConfig({ providerCode, status: "inactive" }));
        return;
      }
    }
    dispatch(startLoading());
    try {
      const res = await axios.get(`cashdrawer/config/${providerCode}`);
      dispatch(setConfig(res.data));
    } catch (e) {
      dispatch(
        hasError(e?.response?.data?.message || e?.message || "Error al cargar la configuración del cajón")
      );
    }
  };

export const saveCashDrawerConfig = (providerCode, configJson) => async (dispatch) => {
  await axios.post("cashdrawer/config", { providerCode, configJson });
  // Force refetch — la config acaba de cambiar en backend.
  await dispatch(fetchCashDrawerConfig(providerCode, { force: true }));
};

export const testCashDrawerConnection = () => async (dispatch) => {
  try {
    const res = await axios.get("cashdrawer/test");
    dispatch(setTestResult(res.data));
    return res.data;
  } catch (e) {
    // Defensivo: `e` puede ser undefined (algún interceptor axios que
    // devuelve null), sin `response` (Network Error) o sin `message`.
    // Devolvemos siempre un objeto {success, message} válido para
    // que el caller no tenga que hacer null-check adicional.
    const msg =
      e?.response?.data?.message
      || e?.response?.data?.error
      || e?.message
      || "No se pudo contactar con el servidor";
    const r = { success: false, message: msg };
    dispatch(setTestResult(r));
    return r;
  }
};

// Crea la transacción — el back devuelve 202 con transactionId. El
// resultado real llega por SignalR (hub) al slice.
//
// `simulate` (opcional): { scenario, delayMs, amountReceivedCents?, customXml? }
// activa el modo test end-to-end. El backend usa filename T{ts}.txt
// (excluido del arqueo) y el Worker genera él mismo la respuesta
// ficticia. El resto del flujo (parser, hub, upsertTx) es el real.
export const chargeCashDrawer = ({ amountCents, sessionId, pedidoId, type = "sale", simulate }) => async (dispatch) => {
  const body = { amountCents, sessionId, pedidoId, type };
  if (simulate) body.simulate = simulate;
  const res = await axios.post("cashdrawer/charge", body);
  // Semilla en el mapa pendingTx para que el PaymentDialog pueda
  // suscribirse por transactionId inmediatamente.
  if (res?.data?.transactionId) {
    dispatch(upsertTx({
      id: res.data.transactionId,
      status: res.data.status,
      fileName: res.data.fileName,
      amountRequestedCents: amountCents,
      type,
      isSimulated: !!simulate,
    }));
  }
  return res.data;
};

export const cancelCashDrawer = (transactionId) => async () => {
  await axios.post(`cashdrawer/cancel/${transactionId}`);
};

// Probe TCP/ICMP directo al hardware (no al Worker). Rápido (~1s), se
// usa desde el botón "Probar dispositivo" del dialog. Devuelve siempre
// {supported, reachable, method, latencyMs?, port?, message} — el
// caller no necesita null-check.
export const probeCashDrawerDevice = () => async () => {
  try {
    const res = await axios.get("cashdrawer/device-probe");
    return res.data || { supported: false, reachable: false, method: "none", message: "Sin respuesta" };
  } catch (e) {
    const msg =
      e?.response?.data?.message
      || e?.response?.data?.error
      || e?.message
      || "No se pudo contactar con el servidor";
    return { supported: false, reachable: false, method: "error", message: msg };
  }
};

export const linkCashDrawerPayment = ({ transactionId, paymentHistoryId, pedidoId }) => async () => {
  await axios.post("cashdrawer/link-payment", { transactionId, paymentHistoryId, pedidoId });
};

export const fetchCashDrawerTransactions = ({ sessionId, status, from, to, take = 100 } = {}) => async (dispatch) => {
  const params = new URLSearchParams();
  if (sessionId) params.append("sessionId", sessionId);
  if (status) params.append("status", status);
  if (from) params.append("from", from);
  if (to) params.append("to", to);
  if (take) params.append("take", String(take));
  const res = await axios.get(`cashdrawer/transactions?${params.toString()}`);
  dispatch(setHistory(res.data || []));
  return res.data;
};
